import React, { useState, useEffect } from 'react';
import { Spinner } from 'spin.js';
import Toastify from "toastify-js";
import currency from "currency.js";
import { useCart } from './CartContext';

const LoadingPanel = ({ loading_payment }) => {
  if (!loading_payment) {
    return null;
  }
  return (
    <div className="loading-panel">
      <h3 className="lp-heading">Adding to Cart</h3>
      <div id="loading-spinner"></div>
      <br />
      <p className="lp-content">Please do not reload the page.</p>
    </div>
  );
};

const AddToCartForm = ({
  step,
  nextStep,
  advanceStepByTwo,
  paymentIntentId,
  buttonStyles,
  primary_price_point,
  selected_price_point_id,
  price_points,
  price_cents,
  camper_identifier,
  fee_percentage,
  fee_breakout,
  campers,
  camp_id,
  csrfToken,
  waitlist_invitation,
  waitlist_entry_id
}) => {
  const { addItem, refreshCart } = useCart();
  const [state, setState] = useState({
    current_camper: '',
    liability_waiver: false,
    shirt_size: '',
    custom_data: {},
    graduation_year: '',
    user_notes: '',
    liability_waiver_signature: '',
    liability_waiver_initials: '',
    disabled: false,
    loading_payment: false,
    alt_button_styles: {
      color: buttonStyles.backgroundColor,
      backgroundColor: buttonStyles.color,
      border: `2px solid ${buttonStyles.backgroundColor}`,
    },
  });

  // Set initial camper attributes and either preserve the existing camper ID or fetch a new one.
  useEffect(() => {
    let isMounted = true;
    const setInitialState = async () => {
      const currentPlayer = campers[camper_identifier];
      if (isMounted) setPlayerAttributes(currentPlayer);
      // Check if camper_identifier appears to be a valid existing camper ID (for example, if length > 10)
      if (camper_identifier && camper_identifier.length > 10) {
        // Use the existing camper ID
        setState(prevState => ({ ...prevState, current_camper: camper_identifier }));
      } else {
        // Create a new camper if no valid ID is provided
        await fetchCamper(currentPlayer, camper_identifier);
      }
    };
    setInitialState();
    return () => {
      isMounted = false;
    };
  }, [camper_identifier, campers]);

  const setPlayerAttributes = (player) => {
    const shirtSize = player?.shirt_size || '';
    const customData = player?.custom_data || {};
    const gradYear = player?.graduation_year || '';
    const userNotes = player?.user_notes || '';
    const liabilityWaiverSignature = player?.liability_waiver_signature || '';
    const liabilityWaiverInitials = player?.liability_waiver_initials || '';
    setState(prevState => ({
      ...prevState,
      shirt_size: shirtSize,
      custom_data: customData,
      graduation_year: gradYear,
      user_notes: userNotes,
      liability_waiver_signature: liabilityWaiverSignature,
      liability_waiver_initials: liabilityWaiverInitials
    }));
  };

  const updateCartCountDisplay = (newCount) => {
    const countElements = document.querySelectorAll('.cart-items-count');
    countElements.forEach(element => {
      element.textContent = newCount;
    });
  };

  const addToCart = async (event) => {
    if (event) {
      event.preventDefault();
    }
    setState(prevState => ({ ...prevState, loading_payment: true }));

    const registrationData = {
      registration: {
        camper_id: state.current_camper,
        liability_waiver: state.liability_waiver,
        shirt_size: state.shirt_size,
        graduation_year: state.graduation_year,
        user_notes: state.user_notes,
        liability_waiver_signature: state.liability_waiver_signature,
        liability_waiver_initials: state.liability_waiver_initials,
        price_point_id: selected_price_point_id,
        waitlist_invitation: waitlist_invitation,
        waitlist_entry_id: waitlist_entry_id,
        custom_data: state.custom_data,
      }
    };

    try {
      const response = await fetch(`/camps/${camp_id}/registrations`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(registrationData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${data.error || 'Unknown error'}`);
      }
      updateCartCountDisplay(data.cart_items_count);
      refreshCart();
      Toastify({
        text: "Successfully added to cart.",
        duration: 3000,
      }).showToast();
      advanceStepByTwo?.();
    } catch (error) {
      console.error('Error adding to cart:', error);
      Toastify({
        text: `Error: ${error.message}`,
        duration: 3000,
      }).showToast();
    } finally {
      setState(prevState => ({ ...prevState, loading_payment: false }));
    }
  };

  const addToCartAndCheckout = async (event) => {
    if (event) {
      event.preventDefault();
    }
    setState(prevState => ({ ...prevState, loading_payment: true }));

    const registrationData = {
      registration: {
        camper_id: state.current_camper,
        liability_waiver: state.liability_waiver,
        shirt_size: state.shirt_size,
        graduation_year: state.graduation_year,
        user_notes: state.user_notes,
        liability_waiver_signature: state.liability_waiver_signature,
        liability_waiver_initials: state.liability_waiver_initials,
        price_point_id: selected_price_point_id,
        waitlist_invitation: waitlist_invitation,
        waitlist_entry_id: waitlist_entry_id,
        custom_data: state.custom_data,
      }
    };

    try {
      const response = await fetch(`/camps/${camp_id}/registrations`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(registrationData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${data.error || 'Unknown error'}`);
      }
      updateCartCountDisplay(data.cart_items_count);
      Toastify({
        text: "Successfully added to cart.",
        duration: 3000,
      }).showToast();

      const cartResponse = await fetch('/carts/show.json');
      const cartData = await cartResponse.json();
      addItem(cartData);

      const paymentIntentResponse = await fetch('/update_checkout', {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ payment_intent_id: paymentIntentId })
      });
      const paymentIntentData = await paymentIntentResponse.json();
      if (!paymentIntentResponse.ok) {
        throw new Error(`Failed to update payment intent: ${paymentIntentData.error || 'Unknown error'}`);
      }
      nextStep();
    } catch (error) {
      console.error('Error adding to cart:', error);
      Toastify({
        text: `Error: ${error.message}`,
        duration: 3000,
      }).showToast();
    } finally {
      setState(prevState => ({ ...prevState, loading_payment: false }));
    }
  };

  // Modified fetchCamper now distinguishes between updating an existing camper and creating a new one.
  const fetchCamper = async (player, existingCamperId) => {
    const shouldUpdate = existingCamperId && existingCamperId.length > 10;
    const endpoint = shouldUpdate ? `/campers/${existingCamperId}` : "/campers";
    const method = shouldUpdate ? 'PATCH' : 'POST';
    try {
      const response = await fetch(endpoint, {
        method: method,
        body: JSON.stringify({ camper: player }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`HTTP error ${response.status}: ${errorData.error || 'Unknown error'}`);
      }
      const data = await response.json();
      setState(prevState => ({ ...prevState, current_camper: data.id, liability_waiver: true }));
    } catch (error) {
      console.error('Fetch request failed: ', error);
    }
  };

  useEffect(() => {
    const target = document.getElementById("loading-spinner");
    if (target != null) {
      const pinWheel = new Spinner({ color: "#fff", lines: 10, speed: 1 }).spin(target);
      return () => {
        pinWheel.stop();
      };
    }
  }, []);

  let url;
  if (window.location.pathname.includes('/waitlist_invitations/')) {
    let id = window.location.pathname.split('/')[2];
    url = `/waitlist_invitations/${id}/update`;
  } else {
    url = `/camps/${camp_id}/registrations`;
  }

  return (
    <form
      className="new-step-form"
      width="100%"
      id="new_registration"
      method="POST"
      action={`${url}`}
    >
      <div className="purchase-recap price-point-options">
        <div className="payment-column">
          <input name="utf8" type="hidden" value="✓" />
          <input name="authenticity_token" type="hidden" value={csrfToken} />
          <input name="registration[camper_id]" type="hidden" value={state.current_camper} />
          <input name="registration[price_point_id]" type="hidden" value={selected_price_point_id} />
          <input name="registration[liability_waiver]" type="hidden" value={state.liability_waiver} />
          <input name="registration[shirt_size]" type="hidden" value={state.shirt_size} />
          <input name="registration[graduation_year]" type="hidden" value={state.graduation_year} />
          <input name="registration[user_notes]" type="hidden" value={state.user_notes} />
          <input name="registration[liability_waiver_signature]" type="hidden" value={state.liability_waiver_signature} />
          <input name="registration[liability_waiver_initials]" type="hidden" value={state.liability_waiver_initials} />
          <input name="registration[custom_data]" type="hidden" value={JSON.stringify(state.custom_data)} />
          <LoadingPanel loading_payment={state.loading_payment} />
        </div>
        <div className="camper-info-row">
          <button
            type="button"
            className="button button-primary create payment-button"
            style={{ ...buttonStyles, backgroundColor: '#4CAF50', color: '#FFF' }}
            disabled={state.disabled || !state.current_camper || state.loading_payment}
            onClick={addToCart}
          >
            Add To Cart
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddToCartForm;
